import { useState, useEffect, useCallback } from 'react';
import type { SubmissionsResponse, SubmissionsFilters } from '../types/submissions';

interface UseSubmissionsResult {
  data: SubmissionsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSubmissions(filters: SubmissionsFilters = {}): UseSubmissionsResult {
  const [data, setData] = useState<SubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (filters.month) params.set('month', filters.month);
    if (filters.department) params.set('department', filters.department);
    if (filters.limit) params.set('limit', String(filters.limit));

    const url = `/api/submissions${params.toString() ? `?${params}` : ''}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<SubmissionsResponse>;
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError((err as Error).message || 'Failed to load submissions');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [filters.month, filters.department, filters.limit, tick]);

  return { data, loading, error, refetch };
}
